import Service, { service } from '@ember/service';
import Store from '@ember-data/store';
import UserModel from 'donor-elf-web/models/user';
import { tracked } from '@glimmer/tracking';
import Bugsnag from '@bugsnag/js';

interface OrganizationNames {
  spo?: boolean;
  intervarsity?: boolean;
  ruf?: boolean;
  yl?: boolean;
  eastwest?: boolean;
}

export default class CurrentUser extends Service {
  @service declare session: any;
  @service declare store: Store;

  @tracked id?: string;
  @tracked firstName?: string;
  @tracked lastName?: string;
  @tracked email?: string;
  @tracked role?: string;
  @tracked selectedFundId?: string;
  @tracked hasMultipleFunds?: boolean;
  @tracked hasMultipleCoachingIndividuals?: boolean;
  @tracked coachingOnly?: boolean;
  @tracked afterLoginRoute?: string;
  @tracked stripeIsDelinquent?: boolean;
  @tracked organizationId?: string;
  @tracked organizationName: OrganizationNames = {}; // not to be confused with organizationDatabaseName (this could be refactored later to prevent confusion)
  @tracked organizationDatabaseName?: string; // the user.organization.name in the database

  get isSiteAdmin() {
    return this.email?.endsWith('admin@donorelf.com') === true;
  }

  get isDemoUser() {
    return this.email === 'demo@donorelf.com';
  }

  get canViewAllFunds() {
    return this.organizationName.intervarsity && this.hasMultipleFunds;
  }

  // Quick duct tape fix for now
  get newsletterSyncEnabled() {
    return !this.isDemoUser && !this.organizationName.yl;
  }

  _loadPromise?: Promise<UserModel | undefined>;
  load(): Promise<UserModel | undefined> {
    if (!this.session.isAuthenticated) {
      return Promise.resolve(undefined);
    }
    if (this._loadPromise) {
      return this._loadPromise;
    }
    return (this._loadPromise = this.store.query('user', {}).then((records) => {
      let user = records.slice()[0];
      if (user) {
        this.id = user.id;
        this.firstName = user.firstName;
        this.lastName = user.lastName;
        this.email = user.email;
        this.role = user.role;
        this.selectedFundId = user.selectedFundId;
        this.hasMultipleFunds = user.hasMultipleFunds;
        this.hasMultipleCoachingIndividuals =
          user.hasMultipleCoachingIndividuals;
        this.coachingOnly = user.coachingOnly;
        this.afterLoginRoute = user.afterLoginRoute;
        this.stripeIsDelinquent = user.stripeIsDelinquent;
        this.organizationId = user.organizationId;
        this.organizationDatabaseName = user.organizationName;
        this.organizationName = {
          spo: user.organizationId === '72',
          intervarsity: user.organizationId === '164',
          ruf: user.organizationId === '366',
          yl: user.organizationId === '400',
          eastwest: user.organizationId === '403',
        };

        if (Bugsnag.isStarted()) {
          Bugsnag.setUser(user.id, user.email, user.name);
        }
      }

      return user;
    }));
  }

  unload() {
    this.id = undefined;
    this.firstName = undefined;
    this.lastName = undefined;
    this.email = undefined;
    this.role = undefined;
    this.selectedFundId = undefined;
    this.hasMultipleFunds = undefined;
    this.hasMultipleCoachingIndividuals = undefined;
    this.coachingOnly = undefined;
    this.afterLoginRoute = undefined;
    this.stripeIsDelinquent = undefined;
    this.organizationId = undefined;
    this.organizationName = {};
    this._loadPromise = undefined;
  }
}
